import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import useSWR from "swr";
import { getSelectedVehicleDetailsApiPath } from "./selectedVehicleDetails.js";
import { useSelectedTripShape } from "./useSelectedTripShape.js";
export async function fetchSelectedVehicleResource(url) {
  const response = await fetch(url);
  if (!response.ok) {
    const error = new Error("Vehicle details are unavailable.");
    error.status = response.status;
    throw error;
  }
  return response.json();
}

// All maps share one live request, freshness policy and static shape cache.
// Do not retain the previous vehicle's details while a new selection loads.
export function useSelectedVehicleDetails(t0) {
  const $ = _c(25);
  const {
    selectedItem,
    isDark,
    routeDisplayOptions
  } = t0;
  let t1;
  if ($[0] !== selectedItem) {
    t1 = getSelectedVehicleDetailsApiPath(selectedItem);
    $[0] = selectedItem;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      refreshInterval: 15000,
      dedupingInterval: 10000,
      revalidateOnFocus: true,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      keepPreviousData: false,
      shouldRetryOnError: false
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const resource = useSWR(t1, fetchSelectedVehicleResource, t2);
  const data = resource.error ? null : resource.data;
  const shapeId = data?.trip?.shape_id;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 3600000,
      keepPreviousData: false
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const shapeResource = useSWR(shapeId ? `/api/shapes/${encodeURIComponent(shapeId)}` : null, fetchSelectedVehicleResource, t3);
  let t4;
  if ($[4] !== data || $[5] !== shapeResource.data) {
    t4 = data ? {
      ...data,
      shape: Array.isArray(shapeResource.data) ? shapeResource.data : []
    } : null;
    $[4] = data;
    $[5] = shapeResource.data;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const itemDetails = t4;
  let t5;
  if ($[7] !== isDark || $[8] !== itemDetails || $[9] !== routeDisplayOptions || $[10] !== selectedItem) {
    t5 = {
      selectedItem,
      isDark,
      tripDetails: itemDetails,
      routeDisplayOptions
    };
    $[7] = isDark;
    $[8] = itemDetails;
    $[9] = routeDisplayOptions;
    $[10] = selectedItem;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    selectedTripData,
    selectedTripShape
  } = useSelectedTripShape(t5);
  const t6 = data?.trip ?? null;
  const t7 = data?.route ?? data?.trip ?? null;
  let t8;
  if ($[12] !== data) {
    t8 = data?.vehicle ? {
      vehicle: data.vehicle
    } : null;
    $[12] = data;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = data?.realtime ?? null;
  const t10 = data?.liveStop ?? null;
  let t11;
  if ($[14] !== itemDetails || $[15] !== resource.error || $[16] !== resource.isLoading || $[17] !== selectedTripData || $[18] !== selectedTripShape || $[19] !== t10 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t11 = {
      itemDetails,
      itemDetailsError: resource.error,
      itemDetailsIsLoading: resource.isLoading,
      selectedVehicleTripDetails: t6,
      selectedVehicleRouteDetails: t7,
      busVehicleDetails: t8,
      busVehicleDetailsLoading: resource.isLoading,
      selectedVehicleUpdate: t9,
      selectedVehicleUpdateIsLoading: resource.isLoading,
      selectedVehicleLiveStopDetails: t10,
      selectedVehicleLiveStopDetailsIsLoading: resource.isLoading,
      selectedTripData,
      selectedTripShape
    };
    $[14] = itemDetails;
    $[15] = resource.error;
    $[16] = resource.isLoading;
    $[17] = selectedTripData;
    $[18] = selectedTripShape;
    $[19] = t10;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}